class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.initEventListeners();
    this.setHeaderCartIconAccessibility();
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-drawer-discount]')) return;
      event.preventDefault();
      const code = new FormData(event.target).get('discount').trim();
      if (code) this.updateDrawer('discount', code);
    });
    this.addEventListener('click', (event) => {
      const add = event.target.closest('[data-drawer-add]');
      const remove = event.target.closest('[data-drawer-remove-code]');
      if (add) this.updateDrawer('add', add.dataset.drawerAdd);
      if (remove) this.updateDrawer('remove', remove.dataset.drawerRemoveCode);
    });
  }

  initEventListeners() {
    const drawer = this.querySelector('#CartDrawer');
    if (!drawer) return;
    drawer.setAttribute('tabindex', '-1');
    drawer.setAttribute('aria-labelledby', 'shoppingCart');
    const viewport = window.visualViewport;
    const fitViewport = () => {
      if (viewport.scale !== 1 || viewport.height >= window.innerHeight) {
        drawer.style.removeProperty('height');
        drawer.style.removeProperty('top');
        return;
      }
      drawer.style.height = `${viewport.height}px`;
      drawer.style.top = `${viewport.offsetTop}px`;
      if (drawer.contains(document.activeElement) && document.activeElement.matches('input')) {
        document.activeElement.scrollIntoView({ block: 'nearest' });
      }
    };
    drawer.addEventListener('shown.bs.offcanvas', () => {
      viewport?.addEventListener('resize', fitViewport);
      viewport?.addEventListener('scroll', fitViewport);
      if (viewport) fitViewport();
      document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    });
    drawer.addEventListener('hidden.bs.offcanvas', () => {
      viewport?.removeEventListener('resize', fitViewport);
      viewport?.removeEventListener('scroll', fitViewport);
      drawer.style.removeProperty('height');
      drawer.style.removeProperty('top');
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
      if (this.activeElement?.isConnected) this.activeElement.focus({ preventScroll: true });
    });
    this.addEventListener('click', (event) => {
      if (!event.target.closest('.btn-close')) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || !window.bootstrap?.Offcanvas) return;
    cartLink.removeAttribute('data-bs-toggle');
    cartLink.removeAttribute('data-bs-target');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code !== 'Space') return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  open(triggeredBy) {
    const drawer = this.querySelector('#CartDrawer');
    if (!drawer || !window.bootstrap?.Offcanvas) {
      window.location.href = routes.cart_url;
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    window.bootstrap.Offcanvas.getOrCreateInstance(drawer).show(triggeredBy);
  }

  close() {
    const drawer = this.querySelector('#CartDrawer');
    if (drawer) window.bootstrap?.Offcanvas.getInstance(drawer)?.hide();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, open = true) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      if (!sectionElement || !html) return;
      const contents = this.getSectionInnerHTML(html, section.selector);
      if (contents === undefined) return;
      if (section.id === 'cart-drawer') this.replaceDrawerContents(sectionElement, contents);
      else sectionElement.innerHTML = contents;
    });
    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
    if (open) this.open();
  }

  replaceDrawerContents(drawer, html) {
    const scrollTop = drawer.querySelector('.drawer__inner').scrollTop;
    const discountOpen = drawer.querySelector('.iglam-drawer-discount')?.open;
    drawer.innerHTML = html;
    const discount = drawer.querySelector('.iglam-drawer-discount');
    if (discount && discountOpen) discount.open = true;
    drawer.querySelector('.drawer__inner').scrollTop = scrollTop;
  }

  async updateDrawer(action, value) {
    if (this.drawerRequest || this.quantityRequests) return;
    const previousButton = document.activeElement;
    if (action !== 'add') this.querySelector('#CartDrawer-Discount')?.blur();
    this.drawerRequest = true;
    const setBusy = (busy) => {
      this.querySelector('#CartDrawer')?.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-drawer-add], [data-drawer-remove-code], [data-drawer-discount] button')
        .forEach((button) => { button.disabled = busy; });
    };
    setBusy(true);
    const sections = this.getSectionsToRender().map(({ id }) => id);
    const root = window.Shopify.routes.root;
    let message = '';
    try {
      const body = { sections, sections_url: window.location.pathname };
      if (action === 'add') {
        body.items = [{ id: Number(value), quantity: 1 }];
      } else {
        const response = await fetch(root + 'cart.js', { cache: 'no-store' });
        if (!response.ok) throw new Error('Could not read the cart.');
        const cart = await response.json();
        const codes = (cart.discount_codes || [])
          .map(({ code }) => code)
          .filter((code) => code.toUpperCase() !== value.toUpperCase());
        if (action === 'discount') codes.push(value);
        body.discount = codes.join(',');
      }
      const response = await fetch(root + (action === 'add' ? 'cart/add.js' : 'cart/update.js'), {
        ...fetchConfig(),
        body: JSON.stringify(body),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || 'Could not update the cart.');
      if (state.sections?.['cart-drawer'] && state.sections?.['cart-icon-bubble']) {
        this.renderContents(state, false);
      } else {
        await this.refreshDrawer();
      }
      if (action === 'discount') {
        const applied = state.discount_codes?.some(
          ({ code, applicable }) => applicable && code.toUpperCase() === value.toUpperCase()
        );
        message = applied ? 'Discount applied.' : 'This code is invalid or your cart does not qualify.';
      } else {
        message = action === 'add' ? 'Item added to your cart.' : 'Discount removed.';
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'iglam-drawer', cartData: state });
    } catch (error) {
      message = typeof error.message === 'string' ? error.message : 'Could not update the cart.';
    } finally {
      this.drawerRequest = false;
      setBusy(false);
      const status = this.querySelector(action === 'add' ? '[data-drawer-status]' : '[data-drawer-discount-status]');
      if (status) {
        status.textContent = message;
      }
      if (this.querySelector('#CartDrawer.show') && !previousButton.isConnected) {
        const target = action === 'add'
          ? this.querySelector(`[data-drawer-add="${value}"]`) || this.querySelector('#CartDrawer-Suggestions')
          : this.querySelector('.iglam-drawer-discount summary');
        if (target?.matches('h3')) target.tabIndex = -1;
        (target || this.querySelector('#CartDrawer-Checkout'))?.focus({ preventScroll: true });
      }
    }
  }

  async refreshDrawer() {
    const response = await fetch(window.Shopify.routes.root + 'cart?sections=cart-drawer,cart-icon-bubble', {
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Cart changed. Reopen the cart to check the latest total.');
    const sections = await response.json();
    if (!sections['cart-drawer'] || !sections['cart-icon-bubble']) {
      throw new Error('Cart changed. Refresh the page to check the latest total.');
    }
    this.renderContents({ sections }, false);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);


function onKeyUpEscape(event) {
  if (event.code === 'Escape') {
    event.currentTarget.closest('details').removeAttribute('open');
  }
}


class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
